import React, { useContext, useEffect, useState } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';
import { useQuery, useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';

import ClientContext from 'app/src/contexts/ClientContext';
import { Button } from 'app/src/elements/buttons';
import DataState from 'app/src/elements/DataState';
import ExternalLink from 'app/src/elements/ExternalLink';
import { Fields, prepareInput } from 'app/src/elements/forms';
import {
  Bold, Column, Row, Text, Title, Warning, WarningText
} from 'app/src/styles';
import colors from 'app/src/styles/colors';
import { currency } from 'app/src/utils/formatting';

import { REORDER_TAG_PRODUCTS, CREATE_REORDER_CART } from './queries';

const MIN_TAGS = 10;
const FREE_SHIPPING_SUBTOTAL = 200;
const SHIPPING_FEE = 15;

// Store URLs and feature copy are marketing content maintained here — the
// prices/terms are not pulled from Shopify.
const ADR_URL = 'https://autodealersresource.com/collections/temporary-tags';
const OTT_URL = 'https://shop.oklahomatemptag.com/';

const VENDORS = [
  {
    name: 'Auto Dealers Resource',
    pitch: 'Fast, in-person service — order the way you always have.',
    features: [
      '$5.00 per tag',
      '10% off for OIADA members',
      '15-tag minimum',
      'Pick up in store or ship via UPS',
      'Usually ready the same day, delivered the next',
    ],
    url: ADR_URL,
    cta: 'Order at AutoDealersResource.com',
  },
  {
    name: 'OklahomaTempTag.com',
    pitch: 'Order direct and save — shipped straight to you.',
    features: [
      '$4.25 per tag',
      '10-tag minimum',
      'Free shipping on 50+ tags ($15 under 50)',
      'Shipped to you — no in-store pickup',
      'Ships in 2 to 3 business days',
    ],
    url: OTT_URL,
    cta: 'Order at OklahomaTempTag.com',
  },
];

const Intro = styled(Text)`
  margin-bottom: 24px;
  max-width: 720px;
`;

const Card = styled(View)`
  border-width: 1px;
  border-color: ${colors.button};
  border-style: solid;
  border-radius: 8px;
  padding: 20px;
  gap: 12px;
`;

const CardTitle = styled(Text)`
  font-size: 22px;
  font-weight: 800;
  color: ${colors.header};
`;

const Pitch = styled(Text)`
  font-style: italic;
`;

const FeatureList = styled(View)`
  gap: 6px;
`;

const StoreCta = styled(View)`
  flex-direction: row;
  justify-content: center;
  background-color: ${colors.button};
  padding: 8px 12px;
  border-radius: 6px;
  align-items: center;
  margin-top: 8px;
`;

const StoreCtaText = styled(Text)`
  color: white;
  font-weight: 800;
  font-size: 16px;
  text-align: center;
`;

const Panel = styled(View)`
  margin-top: 40px;
  align-self: center;
  width: 100%;
  max-width: 600px;
  border-width: 1px;
  border-color: ${colors.button};
  border-style: solid;
  border-radius: 8px;
  padding: 24px;
`;

const PanelTitle = styled(Text)`
  font-size: 20px;
  font-weight: 800;
  color: ${colors.header};
  text-align: center;
  margin-bottom: 4px;
`;

const PanelIntro = styled(Text)`
  text-align: center;
  margin-bottom: 20px;
`;

const SummaryRow = styled(View)`
  flex-direction: row;
  justify-content: space-between;
`;

const VendorCard = ({ vendor, isMobile }) => (
  <Column isMobile={isMobile}>
    <Card>
      <CardTitle>{vendor.name}</CardTitle>
      <Pitch>{vendor.pitch}</Pitch>
      <FeatureList>
        {vendor.features.map(feature => (
          <Text key={feature}>• {feature}</Text>
        ))}
      </FeatureList>
      <ExternalLink url={vendor.url}>
        <StoreCta>
          <StoreCtaText>{vendor.cta} ↗</StoreCtaText>
        </StoreCta>
      </ExternalLink>
    </Card>
  </Column>
);

const ReorderTags = () => {
  const { currentUser, activeDealers, isMobile } = useContext(ClientContext);
  const [formStatus, setFormStatus] = useState();
  const [cartErrors, setCartErrors] = useState([]);

  const {
    control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({ defaultValues: { quantity: '' } });

  const values = watch();

  const { data, loading, error } = useQuery(REORDER_TAG_PRODUCTS);

  const [createReorderCart] = useMutation(CREATE_REORDER_CART, {
    onCompleted: data => {
      if (data.createReorderCart.errors.length > 0) {
        setFormStatus(null);
        setCartErrors(data.createReorderCart.errors);
      } else {
        window.location.href = data.createReorderCart.checkoutUrl;
      }
    },
    onError: mutationError => {
      setFormStatus(null);
      setCartErrors([{ message: mutationError.message }]);
    },
  });

  const dealer =
    (activeDealers || []).find(dealer => dealer.id === values.dealerId);
  const licenses =
    (dealer?.licenses || []).filter(license => !license.inactiveAt);

  useEffect(() => {
    setValue('licenseId', 1 === licenses.length ? licenses[0].value : '');
  }, [values.dealerId]);

  if (!currentUser) { return null; }

  const products = data?.reorderTagProducts || [];
  const product = products.find(product => product.id === values.productId);

  const quantity = parseInt(values.quantity, 10) || 0;
  const pricePerTag = product ? parseFloat(product.pricePerTag) : 0;
  const subtotal = quantity * pricePerTag;
  const shipping = subtotal >= FREE_SHIPPING_SUBTOTAL ? 0 : SHIPPING_FEE;
  const estimatedTotal = subtotal + shipping;
  const belowMinimum = quantity > 0 && quantity < MIN_TAGS;
  const canCheckout = !!product && quantity >= MIN_TAGS;

  const fields = {
    dealerId: {
      label: 'Dealership',
      clientSelect: 'activeDealers',
      oneOption: 'no-choice',
      rules: { required: true },
    },
  };

  if (licenses.length > 1) {
    fields.licenseId = {
      label: 'Dealer License',
      select: [{ value: '', label: 'Select' }, ...licenses],
      rules: { required: true },
    };
  }

  fields.productId = {
    label: 'Product',
    select: [{ value: '', label: 'Select' }, ...products],
    rules: { required: true },
  };

  fields.quantity = {
    label: 'Number of Tags',
    type: 'int',
    rules: { required: true },
  };

  const onSubmit = input => {
    setFormStatus('Processing');
    setCartErrors([]);
    createReorderCart({ variables: prepareInput(input, fields) });
  };

  return (
    <View>
      <Title>Order Tags</Title>

      <Intro>
        Two ways to get your temporary tags. Order from Auto Dealers Resource the
        way you always have — or order direct from OklahomaTempTag.com and pay
        less per tag.
      </Intro>

      <Row>
        {VENDORS.map(vendor => (
          <VendorCard key={vendor.name} vendor={vendor} isMobile={isMobile} />
        ))}
      </Row>

      <Panel>
        <PanelTitle>Quick Reorder from OklahomaTempTag.com</PanelTitle>
        <PanelIntro>
          Already know what you need? Pick a dealership and quantity, and we'll
          build your OklahomaTempTag cart ready to check out — no browsing
          required.
        </PanelIntro>

        {0 === activeDealers?.length ? (
          <Text>You must setup a dealer before ordering tags.</Text>
        ) : (
          <DataState data={data} loading={loading} error={error}>
            <Fields
              fields={fields}
              control={control}
              errors={errors}
              setValue={setValue}
            />

            {canCheckout && (
              <View style={{ marginVertical: 16, gap: 6 }}>
                <SummaryRow>
                  <Text>{quantity} tags × {currency(pricePerTag)}</Text>
                  <Text>{currency(subtotal)}</Text>
                </SummaryRow>
                <SummaryRow>
                  <Text>Shipping</Text>
                  <Text>{0 === shipping ? 'Free' : currency(shipping)}</Text>
                </SummaryRow>
                <SummaryRow>
                  <Bold>Estimated total</Bold>
                  <Bold>{currency(estimatedTotal)}</Bold>
                </SummaryRow>
                <Text style={{ fontStyle: 'italic', marginTop: 4 }}>
                  Plus tax, calculated at checkout.
                </Text>
              </View>
            )}

            {belowMinimum && (
              <Text style={{ color: colors.danger, fontWeight: '600', marginTop: 8 }}>
                Minimum order is {MIN_TAGS} tags.
              </Text>
            )}

            {cartErrors.map((cartError, index) => (
              <Warning key={index}>
                <WarningText>{cartError.message}</WarningText>
              </Warning>
            ))}

            <Button
              label="Continue to Checkout"
              onPress={handleSubmit(onSubmit)}
              processing={'Processing' === formStatus}
              disabled={!canCheckout}
              wide
            />
          </DataState>
        )}
      </Panel>
    </View>
  );
};

export default ReorderTags;
